// Terrain-weighted movement for tactical units: travel along streets is cheap,
// cutting across off-street ground (backyards, lots) burns the per-turn move
// budget faster. This is the tunable that makes the real street grid matter —
// the fast route usually follows roads, but cutting across can be a worthwhile
// shortcut past a patrol watching the street.
//
// Enemies do NOT use this: they path along the road graph (via routeTo) and so
// are naturally street-bound and predictable. Only player units can choose to
// eat the off-street penalty.

import { distanceInMeters } from "../road-graph.mjs";
import { distanceToPolylineMeters, distanceToSegmentMeters } from "./fog-geometry.mjs";

export const OFF_STREET_COST_MULTIPLIER = 3;
export const STREET_THRESHOLD_METERS = 8;
// Cap the number of sub-steps per segment so a very long straight order can't
// blow up the loop; each step is at most this many meters of real ground.
const SAMPLE_STEP_METERS = 2;

// True when a coordinate sits within STREET_THRESHOLD_METERS of any corridor
// polyline — i.e. "on the street".
export function isOnStreet(coordinate, corridors, thresholdMeters = STREET_THRESHOLD_METERS) {
  if (!corridors) return false;
  const list = corridors instanceof Map ? corridors.values() : corridors;
  for (const corridor of list) {
    if (!corridor?.coordinates || corridor.coordinates.length < 2) continue;
    if (distanceToPolylineMeters(coordinate, corridor.coordinates) <= thresholdMeters) return true;
  }
  return false;
}

// Walk `entity` along `path`, spending `budgetMeters` of movement where each
// real meter costs 1 on-street and `offStreetMultiplier` off-street. Returns
// { position, pathIndex, reachedEnd } — how far a unit actually gets this turn,
// which the ghost preview and turn resolver both read.
export function advanceWithTerrainCost(entity, path, budgetMeters, {
  corridors,
  offStreetMultiplier = OFF_STREET_COST_MULTIPLIER,
  streetThresholdMeters = STREET_THRESHOLD_METERS,
} = {}) {
  let position = Array.isArray(entity.position) ? [...entity.position] : [...path[0]];
  let pathIndex = Math.max(0, Math.min(entity.pathIndex || 0, path.length - 1));
  let budget = Math.max(0, budgetMeters);

  while (budget > 0 && pathIndex < path.length - 1) {
    const segmentStart = path[pathIndex];
    const target = path[pathIndex + 1];
    const segmentMeters = distanceInMeters(segmentStart, target);
    if (segmentMeters === 0) { pathIndex += 1; position = [...target]; continue; }

    // How far along this segment are we already? (position may be partway in.)
    let traveledMeters = distanceInMeters(segmentStart, position);
    // Sample the remaining segment in small steps so terrain changes mid-segment
    // are priced correctly rather than judged by a single endpoint.
    let stalled = false;
    while (budget > 0 && traveledMeters < segmentMeters) {
      const stepMeters = Math.min(SAMPLE_STEP_METERS, segmentMeters - traveledMeters);
      const midFraction = (traveledMeters + stepMeters / 2) / segmentMeters;
      const midpoint = lerp(segmentStart, target, midFraction);
      const cost = stepMeters * (isOnStreet(midpoint, corridors, streetThresholdMeters) ? 1 : offStreetMultiplier);
      if (cost > budget) {
        traveledMeters += (budget / cost) * stepMeters;
        budget = 0;
        stalled = true;
        break;
      }
      budget -= cost;
      traveledMeters += stepMeters;
    }
    position = lerp(segmentStart, target, Math.min(1, traveledMeters / segmentMeters));
    if (stalled) break;
    if (traveledMeters >= segmentMeters) pathIndex += 1;
  }

  return { position, pathIndex, reachedEnd: pathIndex >= path.length - 1 };
}

function lerp(a, b, t) {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

// Which segment of a polyline a position currently sits on — the index i such
// that position lies on segment [path[i], path[i+1]]. Needed so a unit part-way
// along a multi-point path continues from the right segment on the next turn
// (advanceWithTerrainCost otherwise assumes it starts at path[0]).
export function pathIndexForPosition(path, position) {
  if (!Array.isArray(path) || path.length < 2) return 0;
  let bestIndex = 0;
  let bestDistance = Infinity;
  for (let i = 0; i < path.length - 1; i += 1) {
    const distance = distanceToSegmentMeters(position, path[i], path[i + 1]);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = i;
    }
  }
  return bestIndex;
}
